import { forwardRef, LegacyRef, useEffect, useRef, useState } from 'react'
import { useTable } from 'react-table'
import { TableVirtuoso } from 'react-virtuoso'
import { twMerge } from 'tailwind-merge'

import { RiArrowRightSLine, RiErrorWarningLine } from 'react-icons/ri'

import { GLoading } from '../g-loading'
import { GPagination } from './g-pagination'
import { TableBody } from './table-body'
import { TableHeader } from './table-header'
import { GTableProps, GTableState } from './types'
import { useScrollEdges } from './use-scroll-edges'
import { useVirtuosoScrollParent } from './use-virtuoso-scroll-parent'

/**
 * Width of the expand-chevron cell. Exported because a left-pinned first column
 * has to clear it, and a `freezeLeftOffset` that disagrees with this by even a
 * pixel shows as a text jump the moment the grid scrolls.
 */
export const EXPAND_CELL_WIDTH_PX = { ds: 30, default: 40 }

/** Left chevron cell for an expandable row (only rendered when `renderSubRow` is set). */
function ExpandToggleCell({
	expanded,
	onToggle,
	isDs,
	pinned
}: {
	expanded: boolean
	onToggle: () => void
	isDs: boolean
	/** Rides along with a left-pinned first column, which sits at its width. */
	pinned?: boolean
}) {
	return (
		<td
			className={twMerge(
				'align-middle',
				isDs
					? 'w-[30px] pl-[6px] pr-0 py-4 border-b border-b-t-checkbox'
					: 'w-10 px-2 py-4',
				pinned ? 'sticky left-0 z-20 bg-white' : ''
			)}
		>
			<button
				type="button"
				aria-label={expanded ? 'Collapse row' : 'Expand row'}
				aria-expanded={expanded}
				onClick={onToggle}
				className={
					isDs
						? 'flex h-6 w-6 items-center justify-center rounded-md border border-t-checkbox bg-t-smoke text-t-coupon shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)] hover:bg-table-band'
						: 'flex items-center justify-center text-t-coupon hover:text-t-desc-gray'
				}
			>
				<RiArrowRightSLine
					className={twMerge(
						'h-4 w-4 transition-transform',
						expanded ? 'rotate-90' : ''
					)}
				/>
			</button>
		</td>
	)
}

export function GTable<T>(props: GTableProps<T>) {
	const {
		columns,
		data,
		pageSize,
		totalCount,
		pageListener,
		showHeader = true,
		showPagination = true,
		canBeFiltered = false,
		title,
		containerClassName,
		paginationClassName,
		isLoading = false,
		sortListener,
		sort,
		endReached,
		overscan,
		changePageSize,
		emptyState,
		search,
		rowClassName,
		variant,
		renderSubRow,
		getRowId
	} = props

	const expandable = typeof renderSubRow === 'function'
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
	// `rows` (react-table) is derived from `rowData` state, which updates one render
	// behind the `data` prop — so after `data` shrinks (search/filter), `rows` can be
	// transiently longer than `data`. Guard against the out-of-range `data[rowIndex]`
	// so `getRowId(undefined)` never throws and unmounts the app.
	const rowKey = (rowIndex: number) => {
		const item = data[rowIndex]
		return item && getRowId ? getRowId(item) : String(rowIndex)
	}
	const toggleExpand = (key: string) =>
		setExpandedKeys((prev) => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})

	const [page, setPage] = useState(1)
	const [rowData, setRowData] = useState<Array<any>>([])
	const [columnData, setColumnData] = useState<Array<any>>([])

	const [state, setState] = useState<GTableState>({
		page: 1,
		sortPath: null
	})

	const handlePageChange = (page: number) => {
		setPage(page)
	}

	useEffect(() => {
		setPage(1)
	}, [search])

	const getTableHeader = () => {
		return columns.map((column, index) => {
			return {
				accessor: `col${index + 1}`
			}
		})
	}
	const handleData = () => {
		const columnData = []
		let singleData = {}
		for (let i = 0; i < data.length; i++) {
			for (let j = 0; j < columns.length; j++) {
				singleData = {
					...singleData,
					[`col${j + 1}`]: data[i]
				}
			}
			columnData.push(singleData)
			singleData = {}
		}
		return columnData
	}

	useEffect(() => {
		if (data.length > 0) {
			setRowData(handleData())
			setColumnData(getTableHeader())
		} else {
			setRowData([])
		}
	}, [data, columns])

	const handleSort = (key: string) => {
		setState({ ...state, page: 1 })
		sortListener && sortListener(key)
	}

	const tableInstance = useTable({ columns: columnData, data: rowData })
	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		tableInstance

	// When expandable, each expanded row's sub-content is inserted as its OWN virtuoso
	// item right after the row, so TableVirtuoso measures its (variable) height correctly.
	// Non-expandable tables keep a 1:1 row→item mapping (null here) and are untouched.
	const displayItems: Array<{ kind: 'row' | 'sub'; rowIndex: number }> | null =
		(() => {
			if (!expandable) return null
			const items: Array<{ kind: 'row' | 'sub'; rowIndex: number }> = []
			for (let i = 0; i < rows.length; i++) {
				items.push({ kind: 'row', rowIndex: i })
				if (expandedKeys.has(rowKey(i)))
					items.push({ kind: 'sub', rowIndex: i })
			}
			return items
		})()
	const virtualCount = displayItems ? displayItems.length : rows.length
	// The expand chevron is GTable's own cell, so it has to be pinned by hand when
	// the identity column beside it is — otherwise the chevrons scroll out from
	// under a column that stays put.
	const pinFirst = columns.some((column) => column?.freeze)

	// `table-layout: fixed` hands each column its declared width, but nothing makes
	// the TABLE wider than its container: once the widths add up to more than the
	// viewport the columns are squeezed to fit instead, so the grid never overflows,
	// never scrolls, and a right-pinned group sits over content that can no longer
	// be reached. Floor the table at the sum of its declared widths — the derived
	// form of the Custom Rules grid's hand-written `min-w-[1318px]`.
	//
	// Only when EVERY column declares one: a partial sum is not a width the table
	// would ever have had, and flooring at it would squeeze the auto columns.
	const declaredWidths = columns.map((column) => {
		const width = column?.width?.trim()
		return width?.endsWith('px') ? parseFloat(width) : NaN
	})
	const minTableWidth =
		declaredWidths.length > 0 && declaredWidths.every((width) => width > 0)
			? declaredWidths.reduce((total, width) => total + width, 0) +
			  (expandable
					? variant === 'ds'
						? EXPAND_CELL_WIDTH_PX.ds
						: EXPAND_CELL_WIDTH_PX.default
					: 0)
			: undefined

	const scrollDemoRef = useRef<HTMLDivElement>(null)
	const scrollParent = useVirtuosoScrollParent(scrollDemoRef)
	const virtuosoScrollProps = scrollParent
		? { customScrollParent: scrollParent }
		: { useWindowScroll: true as const }

	// Both scroll edges, not just the left one: a pinned group should only cast its
	// highlight while it actually hides something. `start`/`end` mean "flush
	// against that edge", so each side's highlight is the negation of its own.
	const edge = useScrollEdges(() => scrollDemoRef.current, [rowData, columns])
	const isScrollLeft = !edge.start
	const pinShadow = { left: !edge.start, right: !edge.end }

	return (
		<div className={twMerge('bg-white rounded w-full', containerClassName)}>
			{title && (
				<div className="mb-4">
					<span className="text-t-desc-gray font-medium">{title}</span>
				</div>
			)}
			<div
				className={twMerge(
					'inline-block min-w-full align-middle overflow-x-auto',
					// `overflow-y-clip` stops the implicit vertical scroll container
					// that `overflow-x-auto` would otherwise coerce (visible -> auto on
					// the cross axis), which trapped the wheel and made the page
					// "scroll twice". Vertical scroll stays on <main>. DS variant only.
					variant === 'ds' ? 'ds-table-scroll rounded-t-xl overflow-y-clip' : ''
				)}
				id="scrollDemo"
				ref={scrollDemoRef}
			>
				{((!isLoading && rows?.length > 0) || canBeFiltered) && (
					<TableVirtuoso
						{...virtuosoScrollProps}
						totalCount={virtualCount}
						endReached={() => {
							if (endReached) endReached()
						}}
						{...(overscan && { overscan })}
						components={{
							Table: ({ style, ...props }) => (
								<table
									className="min-w-full table-auto"
									{...getTableProps()}
									{...props}
									style={{
										...style,
										tableLayout: 'fixed',
										// `max()`, not the bare sum: an inline `minWidth` outranks the
										// `min-w-full` class beside it, so flooring at the sum ALONE drops
										// the 100% floor and a `table-layout: fixed` grid with no `width`
										// then collapses to exactly the sum — leaving dead space to the
										// right of the last column on any container wider than that. The
										// two floors never compete: below the sum the px value wins and the
										// columns keep the exact widths the sticky offsets are measured
										// against; above it there is no horizontal scroll, so nothing is
										// pinned over hidden content and the stretch is free.
										...(minTableWidth
											? { minWidth: `max(100%, ${minTableWidth}px)` }
											: {})
									}}
								/>
							),
							TableBody: forwardRef(({ style: _style, ...props }: any, ref) => (
								<tbody
									className={twMerge(
										showHeader
											? 'divide-y divide-t-checkbox border-b border-b-t-checkbox'
											: ''
									)}
									{...getTableBodyProps()}
									{...props}
									ref={ref as LegacyRef<HTMLTableSectionElement> | undefined}
								/>
							)) as any,
							TableHead: forwardRef(({ style: _style, ...props }: any, ref) => (
								<thead
									className="sticky top-0 z-5 text-t-desc-gray"
									{...props}
									ref={ref as LegacyRef<HTMLTableSectionElement> | undefined}
								/>
							)) as any,
							TableRow: (props: { [key: string]: any }) => {
								const index = props['data-index']
								if (displayItems) {
									const item = displayItems[index]
									if (!item) return <tr {...props} />
									// Sub-rows have no react-table row; render a plain <tr>.
									if (item.kind === 'sub') {
										return <tr {...props} className="text-t-default" />
									}
									const row = rows[item.rowIndex]
									const { key, ...rowProps } = row.getRowProps()
									const rowItem = data[item.rowIndex]
									const dynamicRowClass =
										rowClassName && rowItem ? rowClassName(rowItem) : ''
									return (
										<tr
											{...props}
											key={key}
											{...rowProps}
											className={twMerge(
												'text-t-default font-medium',
												dynamicRowClass
											)}
										/>
									)
								}
								const row = rows[index]
								const { key, ...rowProps } = row.getRowProps()
								const rowItem = data[index]
								const dynamicRowClass =
									rowClassName && rowItem ? rowClassName(rowItem) : ''
								return (
									<tr
										{...props}
										key={key}
										{...rowProps}
										className={twMerge(
											'text-t-default font-medium',
											dynamicRowClass
										)}
									/>
								)
							}
						}}
						fixedHeaderContent={() => {
							if (showHeader) {
								return headerGroups.map((headerGroup) => {
									const { key, ...headerProps } =
										headerGroup.getHeaderGroupProps()
									return (
										<tr key={key} {...headerProps}>
											{expandable && (
												<th
													aria-hidden
													className={twMerge(
														variant === 'ds'
															? 'w-[30px] pl-[6px] pr-0 py-3 border-b border-b-t-checkbox bg-table-band'
															: 'w-10 px-2 py-3 border-b border-b-t-checkbox',
														pinFirst ? 'sticky left-0 z-20' : ''
													)}
												/>
											)}
											{headerGroup.headers.map((column, index) => (
												<TableHeader
													key={index}
													column={columns[index]}
													reactColumn={column}
													sort={sort}
													onSort={handleSort}
													isScrolling={isScrollLeft}
													pinShadow={pinShadow}
													variant={variant}
												/>
											))}
										</tr>
									)
								})
							}
						}}
						itemContent={(index) => {
							if (displayItems) {
								const item = displayItems[index]
								if (!item) return null
								// Full-width sub-row spanning the chevron column + every data column.
								if (item.kind === 'sub') {
									const subItem = data[item.rowIndex]
									if (!subItem) return null
									return (
										<td colSpan={columns.length + 1} className="p-0 bg-white">
											{renderSubRow?.(subItem)}
										</td>
									)
								}
								const row = rows[item.rowIndex]
								prepareRow(row)
								const key = rowKey(item.rowIndex)
								return (
									<>
										<ExpandToggleCell
											isDs={variant === 'ds'}
											pinned={pinFirst}
											expanded={expandedKeys.has(key)}
											onToggle={() => toggleExpand(key)}
										/>
										{row.cells.map((cell, i) => (
											<TableBody
												key={i}
												cell={cell}
												column={columns[i]}
												isScrolling={isScrollLeft}
												pinShadow={pinShadow}
												variant={variant}
											/>
										))}
									</>
								)
							}
							const row = rows[index]
							prepareRow(row)
							return row.cells.map((cell, index) => {
								return (
									<TableBody
										key={index}
										cell={cell}
										column={columns[index]}
										isScrolling={isScrollLeft}
										pinShadow={pinShadow}
										variant={variant}
									/>
								)
							})
						}}
					/>
				)}
				{isLoading ? (
					<div className="pb-12 pt-6">
						<GLoading addMargin={false} />
					</div>
				) : data.length === 0 ? (
					emptyState ? (
						emptyState
					) : (
						<div className="flex flex-col items-center justify-center w-full pb-8 pt-8 text-center">
							<RiErrorWarningLine className="w-8 h-8 text-gray-500" />
							<h3 className="mt-2 text-md font-medium text-gray-700">
								No Results
							</h3>
						</div>
					)
				) : null}
			</div>
			{!isLoading && rows?.length > 0 && pageSize && showPagination && (
				<GPagination
					itemsCount={totalCount ? totalCount : data?.length}
					pageSize={pageSize}
					currentPage={page}
					onPageChange={handlePageChange}
					pageListener={pageListener}
					paginationClassName={paginationClassName}
					changePageSize={changePageSize}
					variant={variant}
				/>
			)}
		</div>
	)
}
